import * as SwitchPrimitive from "@radix-ui/react-switch";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";

interface SwitchProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  /** `large` is the spacious variant for the customer flows (e.g. the booking
   *  insurance toggle); `default` is the dense admin/dashboard size. */
  size?: "default" | "large" | undefined;
  /** Forwarded to the underlying switch element for test selection. */
  "data-testid"?: string | undefined;
}

export function Switch({
  checked,
  onChange,
  disabled = false,
  size = "default",
  "data-testid": dataTestid,
}: SwitchProps) {
  const markDirty = useMarkDialogDirty();
  const large = size === "large";
  const trackSize = large ? "h-6 w-12" : "h-4 w-9";
  const thumbSize = large ? "h-7 w-7" : "h-5 w-5";
  const thumbOn = large ? "translate-x-5" : "translate-x-4";

  // The Cargo switch: the ball is taller than the track so it bulges past the
  // top/bottom edges, the track tints light blue (not the accent) when on while
  // the ball is the accent blue, and the ball grows on hover.
  //
  // `brand-tint` rather than `very-light-blue` for the on track: that token is a
  // border colour, so its dark value is near-black — the on state came out
  // *dimmer* than the off state and the switch read as a floating ball.

  const rootClass = `group relative inline-flex ${trackSize} shrink-0 items-center rounded-full transition-colors ${checked ? "bg-brand-tint" : "bg-border"} ${disabled ? "cursor-not-allowed opacity-40" : ""}`;

  const thumbClass = `block ${thumbSize} shrink-0 rounded-full transition-all ${checked ? `${thumbOn} bg-brand` : "translate-x-0 bg-faint"} ${disabled ? "" : "group-hover:scale-110"}`;

  return (
    <SwitchPrimitive.Root
      checked={checked}
      disabled={disabled}
      {...(dataTestid !== undefined && { "data-testid": dataTestid })}
      onCheckedChange={(value) => {
        markDirty?.();
        onChange(value);
      }}
      className={rootClass}
    >
      <SwitchPrimitive.Thumb className={thumbClass} />
    </SwitchPrimitive.Root>
  );
}
